import { apiFetch } from '@/lib/api';
import { cachedFetch, invalidate, SHELL_CACHE_TTL_MS } from '@/lib/requestCache';
import type { OnboardingState } from '@/services/onboardingService';

/** F035 — the structured KB profile, minimum-viable subset.
 *
 * Mirrors the backend ``tenant_kb_profile`` fields the reflective form
 * read/writes (F034 model). The runtime-only fields (slot defs, links,
 * boundaries, voice) are part of the response shape but the minimum form never
 * sets them — they land via F036 enrichment.
 */
export type Offer = 'services' | 'products_physical' | 'products_digital';

export type Modality =
  | 'presencial_fixo'
  | 'domicilio'
  | 'online_live'
  | 'digital_on_demand'
  | 'physical_shipping'
  | 'made_to_order'
  | 'event_dated'
  | 'rental';

/** F076/F077 — the 4 behavioral objective families (legacy 8-way keys were
 * remapped by backend migration 0040; the runtime falls back to
 * `capture_contacts` on unknown values). */
export type Objective = 'capture_contacts' | 'send_to_link' | 'urgent_dispatch' | 'answer_only';

export type PriceType = 'fixed' | 'from' | 'quote' | 'subscription' | 'package';

export interface CatalogItem {
  name: string;
  price?: string;
  price_type?: PriceType;
  duration?: string;
  lead_time?: string;
  description?: string;
}

/** F058 — a single fixed-session entry (one start time + one or more days). */
export interface SessionSchedule {
  days: string[];
  start: string;
  duration_min?: number;
  note?: string;
}

/** Modality-conditional operation fields + payment. All optional; which keys
 * the form surfaces is derived from the selected modalities. */
export interface OperationFields {
  address?: string;
  coverage_area?: string;
  travel_fee?: string;
  online_platform?: string;
  access_method?: string;
  shipping?: string;
  dispatch_time?: string;
  returns?: string;
  order_lead_time?: string;
  event_advance?: string;
  headcount_pricing?: string;
  rental_period?: string;
  rental_deposit?: string;
  working_hours?: Record<string, unknown> | null;
  /** F058 — fixed-session schedule entries. */
  sessions?: SessionSchedule[];
  payment_methods?: string[];
  payment_methods_other?: string[];
}

// ── F036 enrichment sub-types ────────────────────────────────────────────────

export interface FaqEntry {
  question: string;
  answer: string;
}

export interface SlotDefinition {
  key: string;
  label: string;
  type: 'text' | 'phone' | 'email' | 'date' | 'time' | 'number' | 'choice' | 'session';
  required: boolean;
  when_to_ask?: string;
  choices?: string[];
}

export interface LinkLibraryItem {
  label: string;
  url: string;
  when_to_send?: string;
}

export interface BoundaryRule {
  trigger: string;
  response_type:
    | 'escalate_to_human'
    | 'answer_and_close'
    | 'refuse_and_close'
    | 'refer_external'
    | 'never_opine_redirect';
}

/** Partial update payload for enrichment fields (F036). */
export interface KbProfileUpdateRequest {
  differentiators?: string | null;
  team_credentials?: string | null;
  policies?: string | null;
  proactive_notice?: string | null;
  tone?: string | null;
  voice_example?: string | null;
  never_say?: string | null;
  /** F193 — per-tenant welcome opener shape; 'long' is the default. */
  welcome_style?: 'long' | 'short';
  faq?: FaqEntry[];
  forbidden_scope?: string[];
  forbidden_scope_notify?: boolean;
  /** F167 — operational "we don't do X" facts (distinct from forbidden_scope,
   * which redirects/closes; restrictions answer-then-continue). */
  restrictions?: string[];
  persistent_disclaimer?: string | null;
  refer_external_destination?: string | null;
  boundaries?: BoundaryRule[];
  slot_definitions?: SlotDefinition[];
  link_library?: LinkLibraryItem[];
}

export interface KbProfile {
  offers: Offer[];
  modalities: Modality[];
  one_liner: string | null;
  ideal_customer: string | null;
  catalog: CatalogItem[];
  operation: OperationFields;
  bot_goal: Objective | null;
  objective_freetext: string | null;
  handoff_contact: string | null;
  // Enrichment fields (F036) — set via PATCH /onboarding/kb/profile.
  differentiators: string | null;
  /** F075: "Quem conduz" — credentials of who delivers the service. */
  team_credentials: string | null;
  policies: string | null;
  proactive_notice: string | null;
  closing_cta: string | null;
  faq: FaqEntry[];
  slot_definitions: SlotDefinition[];
  link_library: LinkLibraryItem[];
  boundaries: BoundaryRule[];
  forbidden_scope: string[];
  forbidden_scope_notify?: boolean;
  /** F167 — operational "we don't do X" facts (distinct from forbidden_scope,
   * which redirects/closes; restrictions answer-then-continue). */
  restrictions: string[];
  refer_external_destination: string | null;
  persistent_disclaimer: string | null;
  tone: string | null;
  voice_example: string | null;
  never_say: string | null;
  /** F193 — per-tenant welcome opener shape; NOT NULL on the backend
   * (`server_default='long'`), so every profile response carries one. */
  welcome_style: 'long' | 'short';
  updated_at: string | null;
  /** F074: real persisted `len(knowledge_md)` + the backend's current
   * ceiling — the authoritative size (never estimated client-side). */
  knowledge_size?: KnowledgeSize | null;
}

export interface KnowledgeSize {
  chars: number;
  ceiling: number;
}

/** The activation payload — the minimum-viable subset the form commits. */
export interface KbProfileSubmitRequest {
  offers: Offer[];
  modalities: Modality[];
  one_liner?: string;
  ideal_customer?: string;
  catalog: CatalogItem[];
  operation: OperationFields;
  bot_goal: Objective;
  objective_freetext?: string;
  handoff_contact: string;
}

export const kbProfileService = {
  /** F070: cached (30s TTL). */
  get(opts?: { fresh?: boolean }): Promise<KbProfile> {
    return cachedFetch('kb:profile', () => apiFetch<KbProfile>('/onboarding/kb/profile'), {
      ttlMs: SHELL_CACHE_TTL_MS,
      fresh: opts?.fresh,
    });
  },
  async submit(payload: KbProfileSubmitRequest): Promise<OnboardingState> {
    const next = await apiFetch<OnboardingState>('/onboarding/kb/profile', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    // Activation rewrites the assembled KB + advances the funnel and can flip
    // the operational status — drop every dependent shell key.
    invalidate('onboarding:state', 'kb:profile', 'tenant:profile', 'bot:status');
    return next;
  },
  /** F036 — partial enrichment update. Merges the given fields into the
   * existing profile; only changed fields need to be sent. */
  async update(changes: KbProfileUpdateRequest): Promise<KbProfile> {
    const next = await apiFetch<KbProfile>('/onboarding/kb/profile', {
      method: 'PATCH',
      body: JSON.stringify(changes),
    });
    invalidate('kb:profile', 'tenant:profile');
    return next;
  },
};
